/**
 * API client — thin wrapper around fetch that:
 *   1. Prefixes all paths with the API base (proxied via Vite in dev).
 *   2. Attaches the Firebase ID token as a Bearer token.
 *   3. Returns the full ApiResponse envelope — callers access .data.
 */

import { auth } from '../firebase.js';
import type { ApiResponse } from '@gng-ehs/shared/types';
import { checkLicenceStatus } from '@gng-ehs/shared';

/** In production, Vite injects VITE_API_BASE_URL from .env.
 *  In local dev, the Vite proxy forwards /api → localhost:8787. */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');

/** Get the current Firebase ID token (refreshes if needed). */
async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Perform a request and return the full ApiResponse envelope.
 * Callers access `result.data` for the payload.
 * Throws ApiError on !success or non-ok response.
 */
async function request<T>(
  path: string,
  method: Method = 'GET',
  body?: unknown,
): Promise<ApiResponse<T>> {
  const token = await getIdToken();
  const opts: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);

  const res = await fetch(`${BASE}${path}`, opts);
  const json: ApiResponse<T> = await res.json();

  if (!res.ok || !json.success) {
    throw new ApiError(json.error ?? 'Request failed', res.status, json);
  }
  return json;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: ApiResponse,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── PDF download helper ──────────────────────
/**
 * Fetch a PDF as an authenticated blob and trigger a download.
 * Used by `window.open`-style callers that would otherwise send
 * no Authorization header. Returns a Promise so callers can await it.
 */
async function downloadBlob(path: string, filename: string): Promise<void> {
  const token = await getIdToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    // Surface the server's actual error (e.g. "PDF not available", 401, etc.)
    let msg = 'Failed to download PDF';
    try {
      const text = await res.text();
      const json = JSON.parse(text) as ApiResponse;
      if (json.error) msg = json.error;
    } catch { /* binary/empty body — keep default */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  // Prefer the server-provided filename (e.g. certificate number) from the
  // Content-Disposition header; fall back to the default passed by the caller.
  const cd = res.headers.get('Content-Disposition') || res.headers.get('content-disposition') || '';
  const matched = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const effectiveFilename = matched ? matched[1].replace(/^['"]|['"]$/g, '') : filename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = effectiveFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function fetchPdfBlobUrl(path: string): Promise<string> {
  const token = await getIdToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = 'Failed to load PDF preview';
    try {
      const text = await res.text();
      const json = JSON.parse(text) as ApiResponse;
      if (json.error) msg = json.error;
    } catch { /* keep default */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ── Typed API methods ────────────────────────
// All methods return ApiResponse<T>; callers use .data for the payload.
// List endpoints return ApiResponse<unknown[]> — use r.data for the array.

export const api = {
  // Companies
  companies: {
    list: (params?: { search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<unknown[]>('/companies' + qs(params)),
    get: (id: string) => request<unknown>(`/companies/${id}`),
    create: (data: Record<string, unknown>) => request<unknown>('/companies', 'POST', data),
    update: (id: string, data: Record<string, unknown>) =>
      request<unknown>(`/companies/${id}`, 'PUT', data),
    delete: (id: string) => request<void>(`/companies/${id}`, 'DELETE'),
    bulkDelete: (ids: string[]) =>
      request<{ success: boolean; count: number; message: string }>('/companies/bulk-delete', 'POST', { ids }),
    suggestShortCode: (name: string, excludeId?: string) =>
      request<{ short_code: string }>(`/companies/suggest-short-code?name=${encodeURIComponent(name)}${excludeId ? `&exclude_id=${excludeId}` : ''}`),
    checkShortCode: (code: string, name?: string, excludeId?: string) =>
      request<{ available: boolean; message: string; code: string; suggested?: string; existing_company_name?: string }>(`/companies/check-short-code?code=${encodeURIComponent(code)}${name ? `&name=${encodeURIComponent(name)}` : ''}${excludeId ? `&exclude_id=${excludeId}` : ''}`),
  },

  // Equipment Types
  equipmentTypes: {
    list: () => request<Array<{ id: string; key: string; label: string; short_code?: string; load_test_type: string; multiplier?: number; point5_prefix: string; load_unit: string }>>('/equipment-types'),
    create: (data: { name: string; short_code?: string; load_test_type?: string; multiplier?: number; point5_prefix?: string; load_unit?: string }) =>
      request<{ id: string; key: string; label: string; short_code?: string; load_test_type: string; multiplier?: number; point5_prefix: string; load_unit: string }>('/equipment-types', 'POST', data),
  },

  // Test Methods (Form 10 Point 5)
  testMethods: {
    list: () => request<Array<{ id: string; name: string }>>('/test-methods'),
    create: (data: { name: string }) =>
      request<{ id: string; name: string }>('/test-methods', 'POST', data),
  },

  // Equipment
  equipment: {
    list: (params?: { company_id?: string; equipment_type?: string; search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<unknown[]>('/equipment' + qs(params)),
    get: (id: string) => request<unknown>(`/equipment/${id}`),
    create: (data: Record<string, unknown>) => request<unknown>('/equipment', 'POST', data),
    clone: (sourceEquipmentId: string, newTagName: string) =>
      request<unknown>('/equipment/clone', 'POST', { source_equipment_id: sourceEquipmentId, new_tag_name: newTagName }),
    bulkCreate: (data: Record<string, unknown>) =>
      request<{ count: number; items: string[]; message: string }>('/equipment/bulk', 'POST', data),
    update: (id: string, data: Record<string, unknown>) =>
      request<unknown>(`/equipment/${id}`, 'PUT', data),
    delete: (id: string) => request<void>(`/equipment/${id}`, 'DELETE'),
    bulkDelete: (ids: string[]) =>
      request<{ success: boolean; count: number; message: string }>('/equipment/bulk-delete', 'POST', { ids }),
    suggestTagName: (companyId: string, equipmentNameType: string) =>
      request<{ suggested: string; prefix: string }>(`/equipment/suggest-tag-name?company_id=${encodeURIComponent(companyId)}&equipment_name_type=${encodeURIComponent(equipmentNameType)}`),
    checkTagName: (companyId: string, tagName: string, excludeId?: string) =>
      request<{ available: boolean; message: string; code?: string; suggested?: string }>(`/equipment/check-tag-name?company_id=${encodeURIComponent(companyId)}&tag_name=${encodeURIComponent(tagName)}${excludeId ? `&exclude_id=${excludeId}` : ''}`),
  },

  // Personnel
  personnel: {
    list: (params?: { search?: string; role?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<unknown[]>('/personnel' + qs(params)),
    get: (id: string) => request<unknown>(`/personnel/${id}`),
    create: (data: Record<string, unknown>) => request<unknown>('/personnel', 'POST', data),
    update: (id: string, data: Record<string, unknown>) =>
      request<unknown>(`/personnel/${id}`, 'PUT', data),
    delete: (id: string) => request<void>(`/personnel/${id}`, 'DELETE'),
    bulkDelete: (ids: string[]) =>
      request<{ success: boolean; count: number; message: string }>('/personnel/bulk-delete', 'POST', { ids }),
    /** Returns the raw file URL for signature, stamp, or cert. */
    imageUrl: (id: string, kind: 'signature' | 'stamp' | 'cert'): string => `${BASE}/personnel/${id}/${kind}`,
    /** Returns direct preview URL for competency cert. */
    getCertPreviewUrl: (id: string): string => `${BASE}/personnel/${id}/cert`,
    /** Returns full absolute shareable URL for competency cert. */
    getShareableCertUrl: (id: string): string => {
      const path = `${BASE}/personnel/${id}/cert`;
      return path.startsWith('http') ? path : `${window.location.origin}${path}`;
    },
    /** Fetches stored signature, stamp, or cert with authentication and returns a Blob/Data URL. */
    getImageDataUrl: async (id: string, kind: 'signature' | 'stamp' | 'cert' = 'signature'): Promise<string | null> => {
      try {
        const token = await getIdToken();
        const res = await fetch(`${BASE}/personnel/${id}/${kind}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    },
    downloadCertFile: async (id: string, filename = 'competency_certificate') => {
      await downloadBlob(`/personnel/${id}/cert`, filename);
    },
  },

  // Form 9 Certificates (Hoists and Lifts)
  form9: {
    list: (params?: { company_id?: string; equipment_id?: string; personnel_id?: string; financial_year?: string; defect_status?: string; search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<unknown[]>('/form9' + qs(params)),
    get: (id: string) => request<unknown>(`/form9/${id}`),
    create: (data: Record<string, unknown>) => request<unknown>('/form9', 'POST', data),
    update: (id: string, data: Record<string, unknown>) => request<unknown>(`/form9/${id}`, 'PUT', data),
    delete: (id: string) => request<void>(`/form9/${id}`, 'DELETE'),
    bulkDelete: (ids: string[]) =>
      request<{ success: boolean; count: number; message: string }>('/form9/bulk-delete', 'POST', { ids }),
    downloadPdf: (id: string) => downloadBlob(`/form9/${id}/pdf`, `${id}.pdf`),
    getPdfBlobUrl: (id: string) => fetchPdfBlobUrl(`/form9/${id}/pdf`),
  },

  // Form 10 Certificates
  form10: {
    list: (params?: { company_id?: string; equipment_id?: string; personnel_id?: string; financial_year?: string; defect_status?: string; search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<unknown[]>('/form10' + qs(params)),
    get: (id: string) => request<unknown>(`/form10/${id}`),
    create: (data: Record<string, unknown>) => request<unknown>('/form10', 'POST', data),
    update: (id: string, data: Record<string, unknown>) => request<unknown>(`/form10/${id}`, 'PUT', data),
    delete: (id: string) => request<void>(`/form10/${id}`, 'DELETE'),
    bulkDelete: (ids: string[]) =>
      request<{ success: boolean; count: number; message: string }>('/form10/bulk-delete', 'POST', { ids }),
    downloadPdf: (id: string) => downloadBlob(`/form10/${id}/pdf`, `${id}.pdf`),
    getPdfBlobUrl: (id: string) => fetchPdfBlobUrl(`/form10/${id}/pdf`),
    getPreviousInspection: (equipment_id: string, examination_date?: string) =>
      request<{ previous_inspection_details: string }>('/form10/previous-inspection' + qs({ equipment_id, examination_date })),
  },

  // Form 11 Certificates (Pressure Vessels)
  form11: {
    list: (params?: { company_id?: string; search?: string; financial_year?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<unknown[]>('/form11' + qs(params)),
    get: (id: string) => request<unknown>(`/form11/${id}`),
    create: (data: Record<string, unknown>) => request<unknown>('/form11', 'POST', data),
    update: (id: string, data: Record<string, unknown>) => request<unknown>(`/form11/${id}`, 'PUT', data),
    delete: (id: string) => request<void>(`/form11/${id}`, 'DELETE'),
    bulkDelete: (ids: string[]) =>
      request<{ success: boolean; count: number; message: string }>('/form11/bulk-delete', 'POST', { ids }),
    downloadPdf: (id: string) => downloadBlob(`/form11/${id}/pdf`, `${id}.pdf`),
    getPdfBlobUrl: (id: string) => fetchPdfBlobUrl(`/form11/${id}/pdf`),
    getPreviousInspection: (company_id: string, vessel_serial_no?: string, examination_date?: string) =>
      request<{ last_external: string; last_internal: string; last_hydraulic: string; last_ultrasonic: string }>(
        '/form11/previous-inspection' + qs({ company_id, vessel_serial_no, examination_date }),
      ),
    suggestSerialNo: (company_id?: string, vessel_name?: string) =>
      request<{ suggested: string; prefix: string }>(
        '/form11/suggest-serial-no' + qs({ company_id, vessel_name }),
      ),
    checkSerialNo: (company_id: string, serial_no: string, vessel_name?: string, exclude_id?: string) =>
      request<{ available: boolean; message: string; code?: string; suggested?: string }>(
        '/form11/check-serial-no' + qs({ company_id, serial_no, vessel_name, exclude_id }),
      ),
  },

  // Training Certificates
  training: {
    list: (params?: { company_id?: string; faculty_id?: string; financial_year?: string; search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<unknown[]>('/training' + qs(params)),
    get: (id: string) => request<unknown>(`/training/${id}`),
    create: (data: Record<string, unknown>) => request<unknown>('/training', 'POST', data),
    createBulk: (data: Record<string, unknown>) => request<unknown>('/training/bulk', 'POST', data),
    update: (id: string, data: Record<string, unknown>) => request<unknown>(`/training/${id}`, 'PUT', data),
    delete: (id: string) => request<void>(`/training/${id}`, 'DELETE'),
    bulkDelete: (ids: string[]) =>
      request<{ success: boolean; count: number; message: string }>('/training/bulk-delete', 'POST', { ids }),
    downloadPdf: (id: string) => downloadBlob(`/training/${id}/pdf`, `${id}.pdf`),
    getPdfBlobUrl: (id: string) => fetchPdfBlobUrl(`/training/${id}/pdf`),
    getNextCertNumber: (training_date?: string) =>
      request<{ next_sequence: number; next_certificate_number: string }>(
        '/training/next-cert-number' + qs({ training_date }),
      ),
  },

  // Dashboard
  dashboard: {
    renewals: (params?: { status?: string; company_id?: string }) =>
      request<{ data: unknown[]; summary: Record<string, number> }>('/dashboard/renewals' + qs(params)),
    stats: () => request<Record<string, number>>('/dashboard/stats'),
    personnelCompliance: () =>
      request<{
        expired: Record<string, unknown>[];
        expiring_soon: Record<string, unknown>[];
        valid: Record<string, unknown>[];
        summary: { total: number; expired: number; expiring_soon: number; valid: number };
      }>('/dashboard/personnel-compliance'),
    renew: (data: Record<string, unknown>) =>
      request<unknown>('/dashboard/renew', 'POST', data),
    bulkRenew: (data: { type: 'FORM_9' | 'FORM_10' | 'FORM_11'; items: Record<string, unknown>[] }) =>
      request<{ count: number; data: unknown[]; errors?: string[] }>('/dashboard/bulk-renew', 'POST', data),
    companyAnalytics: (params?: import('@gng-ehs/shared').CompanyAnalyticsFilterParams) =>
      request<import('@gng-ehs/shared').CompanyAnalyticsItem[]>('/dashboard/company-analytics' + qs(params as Record<string, unknown>)) as Promise<
        import('@gng-ehs/shared').ApiResponse<import('@gng-ehs/shared').CompanyAnalyticsItem[]> & {
          summary: import('@gng-ehs/shared').CompanyAnalyticsSummary;
          filters: Record<string, unknown>;
        }
      >,
    companyCertificates: (params: {
      company_id: string;
      cert_type?: 'ALL' | 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
      financial_year?: string;
      start_date?: string;
      end_date?: string;
      search?: string;
      status?: 'ALL' | 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE';
    }) =>
      request<import('@gng-ehs/shared').CompanyCertificatesResponse>(
        '/dashboard/company-certificates' + qs(params as Record<string, unknown>)
      ),
  },

  // Settings
  settings: {
    get: () => request<{ data: Record<string, string> }>('/settings'),
    update: (data: Record<string, string>) => request<{ data: Record<string, string> }>('/settings', 'POST', data),
  },

  // User Management & RBAC (Admin)
  users: {
    getMe: () => request<import('@gng-ehs/shared').AppUser>('/users/me'),
    list: () => request<import('@gng-ehs/shared').AppUser[]>('/users'),
    create: (data: Record<string, unknown>) => request<import('@gng-ehs/shared').AppUser>('/users', 'POST', data),
    update: (id: string, data: Record<string, unknown>) => request<import('@gng-ehs/shared').AppUser>(`/users/${id}`, 'PUT', data),
    delete: (id: string) => request<{ success: boolean; message: string }>(`/users/${id}`, 'DELETE'),
  },

  // Trash & Recycle Bin (30-Day Soft Delete)
  trash: {
    list: (params?: { search?: string; entity_type?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) =>
      request<import('@gng-ehs/shared').TrashItem[]>('/trash' + qs(params)) as Promise<
        import('@gng-ehs/shared').ApiResponse<import('@gng-ehs/shared').TrashItem[]> & {
          totalPages: number;
          total: number;
          page: number;
          limit: number;
          summary: import('@gng-ehs/shared').TrashSummary;
        }
      >,
    restore: (id: string) =>
      request<{ entity_type: string; entity_id: string }>(`/trash/${id}/restore`, 'POST'),
    deleteForever: (id: string) =>
      request<void>(`/trash/${id}`, 'DELETE'),
    emptyTrash: (entity_type?: string) =>
      request<{ count: number; message: string }>('/trash/empty', 'POST', { entity_type }),
    bulkRestore: (ids: string[]) =>
      request<{ count: number; errors?: string[]; message: string }>('/trash/bulk-restore', 'POST', { ids }),
    bulkDelete: (ids: string[]) =>
      request<{ count: number; message: string }>('/trash/bulk-delete', 'POST', { ids }),
  },

  // Drafts (Auto-saving & Recovery)
  drafts: {
    list: (params?: { search?: string; entity_type?: string }) =>
      request<import('@gng-ehs/shared').UserDraft[]>('/drafts' + qs(params)) as Promise<
        import('@gng-ehs/shared').ApiResponse<import('@gng-ehs/shared').UserDraft[]> & {
          summary: import('@gng-ehs/shared').DraftSummary;
          total: number;
        }
      >,
    get: (id: string) =>
      request<import('@gng-ehs/shared').UserDraft>(`/drafts/${id}`),
    save: (id: string, data: { entity_type: import('@gng-ehs/shared').DraftEntityType; title: string; subtitle?: string; form_data: Record<string, unknown> }) =>
      request<import('@gng-ehs/shared').UserDraft>(`/drafts/${id}`, 'PUT', data),
    delete: (id: string) =>
      request<{ success: boolean; message: string }>(`/drafts/${id}`, 'DELETE'),
    clearAll: (entity_type?: string) =>
      request<{ success: boolean; count: number; message: string }>('/drafts' + qs({ entity_type }), 'DELETE'),
  },

  // Renewal Emails & Notifications
  renewalEmails: {
    getConfig: () =>
      request<{
        form9: {
          enabled: boolean;
          lead_days: number;
          interval_days: number;
          max_reminders: number;
          cc_emails: string;
          subject: string;
          body_text?: string;
          body_html: string;
          editor_mode?: 'text' | 'html';
        };
        form10: {
          enabled: boolean;
          lead_days: number;
          interval_days: number;
          max_reminders: number;
          cc_emails: string;
          subject: string;
          body_text?: string;
          body_html: string;
          editor_mode?: 'text' | 'html';
        };
        form11: {
          enabled: boolean;
          lead_days: number;
          interval_days: number;
          max_reminders: number;
          cc_emails: string;
          subject: string;
          body_text?: string;
          body_html: string;
          editor_mode?: 'text' | 'html';
        };
        training: {
          enabled: boolean;
          lead_days: number;
          interval_days: number;
          max_reminders: number;
          cc_emails: string;
          subject: string;
          body_text?: string;
          body_html: string;
          editor_mode?: 'text' | 'html';
        };
        smtp: {
          host: string;
          port: number;
          user: string;
        };
      }>('/renewal-emails/config'),

    saveConfig: (data: {
      type: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
      config: {
        enabled: boolean;
        lead_days: number;
        interval_days: number;
        max_reminders: number;
        cc_emails: string;
        subject: string;
        body_text?: string;
        body_html: string;
        editor_mode?: 'text' | 'html';
      };
    }) => request<{ message: string }>('/renewal-emails/config', 'PUT', data),

    preview: (data: {
      certificate_id: string;
      certificate_type: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
      custom_subject?: string;
      custom_body_text?: string;
      custom_body_html?: string;
    }) =>
      request<{
        recipient_email: string;
        cc_emails: string;
        subject: string;
        body_text?: string;
        body_html: string;
        variables: Record<string, string | number>;
      }>('/renewal-emails/preview', 'POST', data),

    sendManual: (data: {
      certificate_id: string;
      certificate_type: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
      recipient_email?: string;
      cc_emails?: string;
      subject?: string;
      body_text?: string;
      body_html?: string;
    }) =>
      request<{
        message: string;
        data?: { id: string; recipient: string; cc?: string; subject: string };
      }>('/renewal-emails/send-manual', 'POST', data),

    previewGroup: (data: {
      company_id: string;
      examination_date: string;
      certificate_ids?: string[];
      certificate_type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
      custom_subject?: string;
      custom_body_text?: string;
      custom_body_html?: string;
    }) =>
      request<{
        recipient_email: string;
        cc_emails: string;
        subject: string;
        body_text?: string;
        body_html: string;
        items_count: number;
        variables: Record<string, string | number>;
      }>('/renewal-emails/preview-group', 'POST', data),

    sendGroupManual: (data: {
      company_id: string;
      examination_date: string;
      certificate_ids?: string[];
      certificate_type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
      recipient_email?: string;
      cc_emails?: string;
      subject?: string;
      body_text?: string;
      body_html?: string;
    }) =>
      request<{
        message: string;
        data?: { recipient: string; cc?: string; subject: string; count: number };
      }>('/renewal-emails/send-group-manual', 'POST', data),

    getHistory: (params?: {
      page?: number;
      limit?: number;
      status?: string;
      type?: string;
      search?: string;
    }) =>
      request<any[]>('/renewal-emails/history' + qs(params)) as Promise<{
        success: boolean;
        data: any[];
        pagination: { page: number; limit: number; total: number; total_pages: number };
      }>,

    testSmtp: (params?: string | { recipient?: string; cc_emails?: string; type?: string }) => {
      const body = typeof params === 'string' ? { recipient: params } : params || {};
      return request<{ message: string }>('/renewal-emails/test-smtp', 'POST', body);
    },
  },

};


function qs(params?: Record<string, unknown>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (!entries.length) return '';
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

// ── Option helpers for AsyncSearchableSelect ─────
// These return { value, label } arrays suitable for dropdown components.

export interface SelectOption {
  value: string;
  label: string;
}

/** Fetch company options with server-side search. */
export async function fetchCompanyOptions(search: string): Promise<SelectOption[]> {
  const r = await api.companies.list({ search: search || undefined, limit: 50 });
  return ((r.data as Record<string, unknown>[]) ?? []).map((c) => ({
    value: c.id as string,
    label: c.short_code ? `${c.company_name} [${c.short_code}]` : (c.company_name as string),
  }));
}

/** Fetch equipment options for a specific company, with server-side search. */
export async function fetchEquipmentOptions(search: string, companyId?: string): Promise<SelectOption[]> {
  const r = await api.equipment.list({ search: search || undefined, company_id: companyId, limit: 50 });
  return ((r.data as Record<string, unknown>[]) ?? []).map((e) => ({
    value: e.id as string,
    label: `${e.internal_id_number as string} — ${e.equipment_short_code as string}`,
  }));
}

/** Fetch personnel options with server-side search and optional role filter. */
export async function fetchPersonnelOptions(search: string, role?: string): Promise<SelectOption[]> {
  const r = await api.personnel.list({ search: search || undefined, role: role || undefined, limit: 50 });
  return ((r.data as Record<string, unknown>[]) ?? []).map((p) => ({
    value: p.id as string,
    label: p.full_name as string,
  }));
}

/** Fetch only Competent Person options with expiry status badge formatting. */
export async function fetchCompetentPersonOptions(search: string): Promise<SelectOption[]> {
  const r = await api.personnel.list({ search: search || undefined, role: 'competent_person', limit: 100 });
  return ((r.data as Record<string, unknown>[]) ?? []).map((p) => {
    const validUpto = (p.competency_valid_upto as string) || '';
    const status = checkLicenceStatus(validUpto);
    let label = (p.full_name as string) || '';
    if (status.isExpired) {
      label = `${label} [🔴 EXPIRED: ${status.formattedDate}]`;
    } else if (status.isExpiringSoon) {
      label = `${label} [⚠️ Exp: ${status.formattedDate} (${status.daysRemaining}d left)]`;
    }
    return {
      value: p.id as string,
      label,
    };
  });
}

/** Fetch only Trainer / Faculty options. */
export async function fetchTrainerOptions(search: string): Promise<SelectOption[]> {
  return fetchPersonnelOptions(search, 'trainer');
}

